import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { apiGet, apiPost } from '../shared/api'
import { useI18n } from '../i18n'

interface MeUser {
  id: string
  email: string
  displayName: string
  role?: 'user' | 'admin'
}

export default function Settings() {
  const { me } = useOutletContext<{ me: MeUser | null }>()
  const { t } = useI18n()

  const [user, setUser] = useState<MeUser | null>(me)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiGet<{ user?: MeUser }>('/api/auth/me')
        setUser(res.user || null)
      } catch {
        setUser(null)
      }
    }
    void load()
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)

    if (!currentPassword || !newPassword) {
      setError(t('settings.pleaseFillPasswords'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.newPasswordMismatch'))
      return
    }
    if (newPassword.length < 8) {
      setError(t('settings.newPasswordMin'))
      return
    }

    setSubmitting(true)
    try {
      await apiPost('/api/auth/password', { currentPassword, newPassword })
      setMessage(t('settings.updatePasswordSuccess'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setError(err?.message || t('settings.updateFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">{t('settings.title')}</h1>
        <div className="text-gray-600">{t('settings.requireLogin')}</div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('settings.title')}</h1>

      <section className="mb-8 bg-white border rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3">{t('settings.userInfo')}</h2>
        <div className="space-y-2 text-gray-700">
          <div>
            <span className="inline-block w-24 text-gray-500">{t('settings.email')}</span>
            <span>{user.email}</span>
          </div>
          <div>
            <span className="inline-block w-24 text-gray-500">{t('settings.nickname')}</span>
            <span>{user.displayName}</span>
          </div>
          <div>
            <span className="inline-block w-24 text-gray-500">{t('settings.role')}</span>
            <span>{user.role || 'user'}</span>
          </div>
        </div>
      </section>

      <section className="bg-white border rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-4">{t('settings.changePassword')}</h2>
        {message && <div className="mb-3 rounded border border-green-200 bg-green-50 text-green-700 px-3 py-2">{message}</div>}
        {error && <div className="mb-3 rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2">{error}</div>}
        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('settings.currentPassword')}</label>
            <input
              type="password"
              className="w-full border rounded-md px-3 py-2"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('settings.newPassword')}</label>
              <input
                type="password"
                className="w-full border rounded-md px-3 py-2"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('settings.confirmNewPassword')}</label>
              <input
                type="password"
                className="w-full border rounded-md px-3 py-2"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
          >
            {submitting ? t('settings.saving') : t('settings.saveChanges')}
          </button>
        </form>
      </section>
    </div>
  )
}
